<template>
  <div :class='{"selected": isSelected, "disabled": isDisabled, "bold": bold}' class="en-tab-header"
       @click="handleClick">
    <slot></slot>
  </div>
</template>

<script setup>

/**
 * Component Props
 *
 * @property {string} title - The title of the tab. Default is 'Tab'.
 * @property {boolean} isSelected - Indicates if the tab is currently selected. Default is false.
 * @property {boolean} isDisabled - Indicates if the tab is disabled and cannot be clicked. Default is false.
 * @property {boolean} bold - Applies bold styling to the tab if true.
 */
// Define props
const props = defineProps({
  title: {
    type: String,
    default: 'Tab',
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  bold: Boolean,
});

/**
 * Component Emits
 *
 * @event click - Emitted when the tab is clicked.
 */
const emit = defineEmits(['click']);

/**
 * Handles the click event.
 * If the tab is not disabled, it emits the 'click' event.
 */
const handleClick = () => {
  if (!props.isDisabled) {
    emit('click');
  }
};
</script>

<style lang="scss">
@use "Styles/helper/variables.scss";
@use "Styles/helper/mixins.scss";

.en-tab-header {
  display: inline-block;
  padding: variables.$spacing-s variables.$spacing-s;
  border-bottom: variables.$en-blue thin solid;
  cursor: pointer;
  line-height: 1rem;
  @include mixins.transition(border 0.1s linear);

  &:hover,
  &:focus,
  &:active,
  &.active {
    border-bottom: variables.$en-blue 4px solid;
  }

  &.disabled {
    color: variables.$en-40 !important;
    border-bottom: variables.$en-40 thin solid !important;
    cursor: default;
    pointer-events: none;
  }

  &.selected {
    border-bottom: variables.$en-blue 4px solid;
  }

  &.bold {
    border-bottom: variables.$en-blue 4px solid;
    font-size: 1.25rem;
    @include mixins.transition(background-color 0.1s linear);
    
    &:hover,
    &:focus,
    &:active,
    &.active {
      background-color: variables.$en-20;
    }
    &.selected {
      background-color: variables.$en-blue;
      color: white;
    }
  }
}
</style>